import { ArrowRightAltOutlined, CloseOutlined } from "@mui/icons-material";
import {
    Avatar,
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    IconButton,
    InputLabel,
    List,
    ListItem,
    ListItemAvatar,
    ListItemButton,
    ListItemText,
    MenuItem,
    Select,
    Step,
    StepLabel,
    Stepper,
    TextField,
    Typography,
} from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import {
    FormFieldErrors,
    getErrors,
    hasErrors,
    useForm,
    validateMandatoryField,
    validateNumberField,
} from "../../utils/form";
import { useUserInfo } from "../../utils/security";
import SliderWithButtons from "../../components/SliderWithButtons";
import { DatePicker } from "@mui/x-date-pickers";
import dayjs from "dayjs";

const TransactionTypesList = ({ onTypeSelected }) => {
    const { userInfo } = useUserInfo();
    return (
        <List>
            {(userInfo?.transactionTypes || [])
                .filter((tt) => !tt.hasIdentificator)
                .map((action) => (
                    <ListItem disableGutters key={action.id}>
                        <ListItemButton
                            onClick={() => {
                                onTypeSelected?.(action);
                            }}>
                            <ListItemAvatar>
                                <Avatar>
                                    <ArrowRightAltOutlined />
                                </Avatar>
                            </ListItemAvatar>
                            <ListItemText primary={action.name} secondary={action.description} />
                        </ListItemButton>
                    </ListItem>
                ))}
        </List>
    );
};

export const RecurringPaymentDialog = ({ open, onClose, onSubmit, data }) => {
    const { userInfo } = useUserInfo();
    const [step, setStep] = useState(0);
    useEffect(() => setStep(0), [open]);

    const mapFieldsToData = useCallback(
        ({ date, ...values }) => ({
            date: date ? dayjs(date).format("YYYY-MM-DD") : undefined,
            ...values,
        }),
        [userInfo]
    );
    const mapDataToFields = useCallback(
        ({ date, ...values }) => ({
            date: date ? dayjs(date, "YYYY-MM-DD") : null,

            ...values,
        }),
        [userInfo]
    );

    const validate = useCallback((values, errors) => {
        validateMandatoryField(values.date, "date", errors);
        validateMandatoryField(values.frequency, "frequency", errors);
        return errors;
    }, []);

    const { values, setValues, handleChange, errors, handleSubmit } = useForm({
        onSubmit,
        validate,
        mapFieldsToData,
        mapDataToFields,
        data,
    });

    return (
        <Dialog onClose={onClose} open={open} maxWidth="sm" fullWidth>
            <DialogTitle>Vytvoření transakcí</DialogTitle>
            <IconButton
                aria-label="close"
                onClick={onClose}
                sx={{
                    position: "absolute",
                    right: 8,
                    top: 8,
                    color: (theme) => theme.palette.grey[500],
                }}>
                <CloseOutlined />
            </IconButton>
            <DialogContent>
                <Box sx={{ mb: 4 }}>
                    <Stepper activeStep={1} alternativeLabel>
                        <Step key={0}>
                            <StepLabel>Typ transakce</StepLabel>
                        </Step>
                        <Step key={1}>
                            <StepLabel>Nastavení opakování</StepLabel>
                        </Step>
                    </Stepper>
                </Box>
                <Box sx={{ my: 2 }}>
                    {step === 0 && (
                        <>
                            <Typography variant="body1">Vyberte typ transakce</Typography>
                            <TransactionTypesList
                                onTypeSelected={(transactionType) => {
                                    setValues({ ...values, transactionType });
                                    setStep(1);
                                }}
                            />
                        </>
                    )}
                    {step === 1 && (
                        <>
                            <TextField
                                readOnly
                                fullWidth
                                label="Typ transakce"
                                value={values.transactionType?.name}
                                margin="normal"
                            />
                            <DatePicker
                                onChange={(date) => {
                                    setValues({
                                        ...values,
                                        date,
                                        fiscalYear: date?.year() || undefined,
                                        dueDate:
                                            date && values.hasDueDate && values.defaultDueDateDays
                                                ? date.add(values.defaultDueDateDays, "days")
                                                : undefined,
                                    });
                                }}
                                value={values.date || null}
                                label="Datum první transakce"
                                format={userInfo.dateFormat}
                                slotProps={{
                                    textField: {
                                        fullWidth: true,
                                        required: true,
                                        error: hasErrors(errors, "date"),
                                        margin: "normal",
                                    },
                                }}
                                helperText={<FormFieldErrors errors={errors} field="date" />}
                            />

                            <FormControl fullWidth margin="normal">
                                <InputLabel>Frekvence opakování</InputLabel>
                                <Select
                                    name="frequency"
                                    value={values.frequency}
                                    onChange={handleChange}
                                    error={hasErrors(errors, "frequency")}>
                                    <MenuItem value="week">Týdně</MenuItem>
                                    <MenuItem value="month">Měsíčně</MenuItem>
                                    <MenuItem value="quarter">Čtvrtletně</MenuItem>
                                    <MenuItem value="year">Ročně</MenuItem>
                                </Select>
                                <FormFieldErrors errors={getErrors(errors, "frequency")} />
                            </FormControl>
                            <Box mb={2}>
                                <SliderWithButtons
                                    value={values.count}
                                    onChange={handleChange}
                                    name="count"
                                    title="Počet transakcí"
                                    minValue={2}
                                    maxValue={24}
                                    step={1}
                                />
                            </Box>
                        </>
                    )}
                </Box>
            </DialogContent>
            <DialogActions sx={{ p: 2, px: 3 }}>
                <Button onClick={onClose}>Storno</Button>
                {step === 1 && (
                    <Button onClick={() => setStep(0)} variant="outlined">
                        Zpět
                    </Button>
                )}
                <Button variant="contained" onClick={handleSubmit} disabled={step !== 1}>
                    Pokračovat
                </Button>
            </DialogActions>
        </Dialog>
    );
};
